import { useEffect, useState, useContext } from "react";
import { useAtom } from "jotai";
import { GlobalVars } from "../../../state/global/globalVars";
import { eventIcons } from "../../../state/schedule/eventIcons";
import { eventStatusAtom } from "../../../state/schedule/eventStatus";
import { installersAtom, installerTypeClassAtom, zonesAtom } from "../../../state/schedule/scheduleAtoms";
import { MessageContext } from "../../../providers/MessageProvider";
import { handleSchedulingApiError } from "../../../utils/errorHandler";

import Dropdown from "../../ui/form/Dropdown";

import UilAngleLeft from "@iconscout/react-unicons/icons/uil-angle-left";
import UilAngleRight from "@iconscout/react-unicons/icons/uil-angle-right";

import dayjs from "dayjs";
import utc from 'dayjs/plugin/utc.js';
import timezone from 'dayjs/plugin/timezone.js';
dayjs.extend(utc);
dayjs.extend(timezone);

const defaultTz = "America/Los_Angeles"

export default function Calendar({ userId, hideNS, setHideNS, setEventModalOpen, setEventEditModalOpen, setEventEditParams, setActiveEventId, setActiveEvent, refreshCalendar, setRefreshCalendar, dropTechExport, setDropTechExport, selectedInstallerTypes }) {
    const [activeDay, setActiveDay] = useState(dayjs());
    const [events, setEvents] = useState([]);
    const [zones, setZones] = useState([]);
    const [zone, setZone] = useState();
    const [statuses] = useAtom(eventStatusAtom);

    const [installers, setInstallers] = useAtom(installersAtom);
    const [installerTypes] = useAtom(installerTypeClassAtom);
    const [installerZones, setInstallerZones] = useState({});

    const { showMessage } = useContext(MessageContext);

    const workDayStartHour = 7; // Start of workday (AM)
    const workDayTotalHours = 12; // Total hours in workday
    const minInstallerClass = "min-w-[160px]";

  // Filter installers based on selectedInstallerTypes
  const filteredInstallers = (installer) =>{
    return installers.filter((installer) =>
        selectedInstallerTypes.includes(installer.InstallerType.name)
      );
    };

    const refreshTimer = 60000 * 1.5; // (Milliseconds in a minute) * (Number of minutes)

    async function handleZoneChange(zoneId, installerId) {
        let zoneObj = installerZones;
        zoneObj[installerId] = parseInt(zoneId);
        setInstallerZones(zoneObj);

        let data = {
            installerId: installerId,
            zoneId: parseInt(zoneId),
            date: activeDay.format('YYYY-MM-DD')
        }
        const req = await fetch(`${process.env.REACT_APP_API_URL}/scheduling/installer-zone`, {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            },
            credentials: 'include',
            body: JSON.stringify(data)
        });

        // Handle errors consistently - navigate back to today's schedule when error occurs
        const errorHandled = await handleSchedulingApiError(req, showMessage, () => setActiveDay(dayjs()));
        if (errorHandled) {
            return; // Stop execution if error was handled
        }
    }

    // Auto refresh every 90 seconds
    useEffect(() => {
        const interval = setInterval(() => {
            getScheduleEventsOnDate();
        }, refreshTimer);

        return () => clearInterval(interval); // Clear interval to prevent memory leaks
    }, [activeDay]);

    useEffect(() => {
        // if (userId) setHideNS(false);

        getScheduleEventsOnDate();
    }, [activeDay, userId, hideNS, setHideNS]);

    useEffect(() => {
        async function getCalendarData() {
            const installersReq = await fetch(`${process.env.REACT_APP_API_URL}/scheduling/installers`, {
                method: 'GET',
                credentials: 'include' // Include credentials in cross-origin requests
            });
            const zonesReq = await fetch(`${process.env.REACT_APP_API_URL}/scheduling/zones`, {
                method: 'GET',
                credentials: 'include' 
            });

        const installersErrorHandled = await handleSchedulingApiError(installersReq, showMessage, () => setActiveDay(dayjs()));
        const zonesErrorHandled = await handleSchedulingApiError(zonesReq, showMessage, () => setActiveDay(dayjs()));
        
        if (installersErrorHandled || zonesErrorHandled) {
            return; // Stop execution if any error was handled
        }

            setInstallers(await installersReq.json());

            let zoneOptions = await zonesReq.json();
            zoneOptions.unshift({"id": 999, "name": "No Assignment"}); 
            setZones(zoneOptions);
        }

        getCalendarData();
    }, [ ]);

    useEffect(() => {
        if (refreshCalendar) {
            setRefreshCalendar(false);
            getScheduleEventsOnDate();
        }
    }, [ refreshCalendar ]);


    useEffect(() => {
        if (dropTechExport) {
            setDropTechExport(false);

            // Ensure unique jobs (remove any extras assigned to multiple techs), then sort by earliest to latest start times
            // TODO: Really should be done on the backend, this is very messy to do on the frontend
            // but this'll be temporary anyway (knock on wood)
            let temp = JSON.parse(JSON.stringify(events));
            const eventIds = temp.map(({ id }) => id);
            const filtered = temp.filter(({ id }, index) => !eventIds.includes(id, index + 1));
            filtered.sort(function(a, b){
                return new Date(a.datetimeStart) - new Date(b.datetimeStart);
            });

            let rows = [["Scheduled Time", "Type", "Customer", "Address", "City", "Technician(s)", "Status", "Notes", "Needs Action Notes"]];
            for (let i = 0, len = filtered.length; i < len; i++) {
                if (filtered[i].ScheduleEventType.name.includes("Blocked Time") || filtered[i].ScheduleEventStatus.name.includes("Time Off")) {
                    continue;
                }

                let startTime = getDayjsFromTime(filtered[i].datetimeStart, true)
                let formattedStartTime = startTime.format("h:mm A");

                let endTime = getDayjsFromTime(filtered[i].datetimeEnd, true)
                let formattedEndTime = endTime.format("h:mm A");

                let techs = [];
                for (let j = 0, len = filtered[i].Installers.length; j < len; j++) {
                    let tech = installers.find(t => t.id === filtered[i].Installers[j].id); // also should just pull more tech info from the backend
                    if (tech?.User) {
                        techs.push(`${tech.User.firstName} ${tech.User.lastName}`);
                    }
                }

                const [streetAddress, city] = (filtered[i].address || "").split(",").map(s => s.trim());
                rows.push([`${formattedStartTime} - ${formattedEndTime}`, filtered[i].ScheduleEventType.name, filtered[i].customerName, streetAddress, city || "", techs.join(" & "), filtered[i].ScheduleEventStatus.name, `"${(filtered[i].notes || "").replace(/"/g, '""')}"`, `"${(filtered[i].failedNotes || "").replace(/"/g, '""')}"`]);
            }

            // Convert to CSV format and download
            const csvString = rows.map(e => e.join(",")).join("\n");
            const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
            const blobUrl = URL.createObjectURL(blob);
            var link = document.createElement("a");
            link.setAttribute("href", blobUrl);
            link.setAttribute("download", `Cityside_Daily_Schedule_${activeDay.format("MM/DD/YYYY")}.csv`);
            document.body.appendChild(link); // Required for firefox
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(blobUrl);
        }
    }, [ dropTechExport ]);


    function handleNewEventClick(event, user, minute) {
        let elmn = document.elementsFromPoint(event.clientX, event.clientY).find((e) => e.hasAttribute("data-hour"));
        let hour = parseInt(elmn.getAttribute("data-hour")) + workDayStartHour;
        setEventEditParams({
            user: user,
            date: activeDay,
            hour: hour,
            minute: minute,
        });
        setEventEditModalOpen(true);
    }

    function handleEventClick(event, eventId) {
        event.stopPropagation();
        setActiveEventId(eventId);
        setEventModalOpen(true);
    }

    async function getScheduleEventsOnDate() {
        // make sure that the user's local browser isn't jumping between dates when converting to ISO
        const eventReq = await fetch(`${process.env.REACT_APP_API_URL}/scheduling/schedule-events-on-date/${activeDay.hour(12)}`, {
            method: 'GET',
            credentials: 'include' // Include credentials in cross-origin requests
        });
        const installerZoneReq = await fetch(`${process.env.REACT_APP_API_URL}/scheduling/installer-zones-on-date/${activeDay.format('YYYY-MM-DD')}`, {
            method: 'GET',
            credentials: 'include' // Include credentials in cross-origin requests
        });

        // Handle errors consistently - navigate back to today's schedule when error occurs
        const eventErrorHandled = await handleSchedulingApiError(eventReq, showMessage, () => setActiveDay(dayjs()));
        const installerZoneErrorHandled = await handleSchedulingApiError(installerZoneReq, showMessage, () => setActiveDay(dayjs()));
        
        if (eventErrorHandled || installerZoneErrorHandled) {
            return; // Stop execution if any error was handled
        }

        const eventRes = await eventReq.json();
        const installerZoneRes = await installerZoneReq.json();

        setEvents(eventRes);
        setInstallerZones(installerZoneRes);
    }

    function getDayjsFromTime(time) {
        return activeDay.hour(dayjs(time).tz(defaultTz).hour()).minute(dayjs(time).tz(defaultTz).minute());
    }

    function changeActiveDayCalendar(e) {
        let calendarDay = dayjs(e.target.value);
        calendarDay = calendarDay.add(12, "hour");  // Calendar picker auto moves hour to 0, which then causes problems if user local time is behind UTC as it dates will rollback one day. Bandaid fix fo sho.

        setActiveDay(calendarDay);
    }


    return (
        <div>
            {/* Header */}
            <div className="flex justify-center items-center border rounded-tl rounded-bl rounded-tr border-light-2 bg-light-8 lg:p-2">
                {/* Previous day button */}
                <button
                    className="p-2 lg:p-4"
                    onClick={() => setActiveDay(activeDay.subtract(1, "day"))}
                >
                    <UilAngleLeft size={GlobalVars.iconSize.xl} />
                </button>

                {/* Datepicker button */}
                <button
                    className="text-sm font-bold px-6"
                    onClick={() => setActiveDay(dayjs())}
                >{activeDay.format("dddd MMMM D, YYYY")}</button>
                <input
                    type="date"
                    className="font-bold w-6 self-center date-sched"
                    onKeyDown={(e) => e.preventDefault()}
                    onChange={(e) => (changeActiveDayCalendar(e))}
                    value={activeDay.format("YYYY-MM-DD")}
                />

                {/* Next day button */}
                <button
                    className="p-2 lg:p-4 ml-2"
                    onClick={() => setActiveDay(activeDay.add(1, "day"))}
                >
                    <UilAngleRight size={GlobalVars.iconSize.xl} />
                </button>
            </div>

            {/* Calendar */}
            <div className="flex overflow-x-auto">
                {/* Hours legend */}
                    {/* Offset for zone dropdown */}
                    <div className="w-18 mt-[3.6rem] min-w-[40px]">
                        <div className={`pr-1 lg:px-2 text-right`}>
                            <span className="text-xs lg:text-sm">
                                &nbsp;
                            </span>
                    </div>
                    {/* Hour labels*/}
                    {
                        Array.from(new Array(workDayTotalHours), (_, index) => (
                            <div key={index} className={`pr-1 lg:px-2 text-right ${index + 1 < workDayTotalHours ? "h-[4rem]" : ""}`}>
                                <span className="text-xs lg:text-sm">
                                    {index + 1 > (12 - workDayStartHour) ? `${index - (12 - workDayStartHour) === 0 ? "12" : index - (12 - workDayStartHour)} PM` : `${index + workDayStartHour} AM`}
                                </span>
                            </div>
                        ))
                    }
                </div>

                {/* Day */}
                <div className="flex-1 border rounded-bl rounded-br border-light-1 border-t-0">
                    {/* Header columns */}
                    <div className="flex justify-between border-b border-light-1">
                        {
                            filteredInstallers().map((installer, index) => (
                                    <h3 key={index} className={`flex-1 ${minInstallerClass} flex justify-center items-center p-1 h-14 ${installerTypes.find((t) => t.name === installer.InstallerType.name)?.class} ${index > 0 && "border-l border-light-1"}`}>
                                        { `${installer.User.firstName} ${installer.User.lastName}`}
                                        <br />
                                        { installer.InstallerType.name }{installer.InstallerType.name === "Region" || installer.InstallerType.name.includes("Contractor") ? "" : " Tech"}
                                    </h3>
                                )
                            )
                        }
                    </div>


                    {/* Timeslot and event container */}
                    <div
                        className="relative"
                        style={{ height: `${workDayTotalHours * 4}rem` }}
                    >
                        {/* Time Slots */}
                        <div className="absolute top-0 w-full">
                            {
                                Array.from(new Array(workDayTotalHours), (_, index) => (
                                    <div
                                        key = { index }
                                        data-hour = { index }
                                        className = { `h-[4rem] w-full ${index !== 0 && "border-t border-dotted border-gray-400" }`}
                                    >
                                    </div>
                                ))
                            }
                        </div>

                        {/* Events */}
                        <div className="absolute flex justify-between h-full w-full">
                            {
                                filteredInstallers().map((user, index) => (
                                    // TODO: Move cancelled stuff to other column?
                                    <div key={index} data-index={index}
                                        className={`grid grid-cols-1 ${minInstallerClass} w-full h-full gap-[0px] lg:gap-0 items-start h-full ${index > 0 && "border-l border-gray-500"}`}
                                    >
                                        <div className="relative w-full col-start-1 row-start-1">
                                        {
                                            Array.from(new Array(workDayTotalHours), (_, index) => (
                                                <div key={index * 100} className="w-full text-right text-transparent">
                                                    {/* Set hovering over calendar to AM/PM format */}
                                                    {/* Enable onclick events */}
                                                    <div onClick={(e) => handleNewEventClick(e, user, "00")} className="w-full cursor-pointer h-[2rem] pr-[20%] pt-[3px] border-b hover:border-l-4 border-l-transparent border-b-dotted border-light-9 text-transparent hover:text-light-5 hover:border-l-light-3/30 hover:bg-light-3/10">{index + workDayStartHour === 12 ? 12 : (index + workDayStartHour) % 12}:00</div>
                                                    <div onClick={(e) => handleNewEventClick(e, user, "30")} className="w-full cursor-pointer h-[2rem] pr-[20%] pt-[3px] border-l-4 border-l-transparent border-light-9 text-transparent hover:text-light-5 hover:border-l-light-3/30 hover:bg-light-3/10">{index + workDayStartHour === 12 ? 12 : (index + workDayStartHour) % 12}:30</div>
                                                </div>
                                            ))
                                        }
                                        </div>
                                        {
                                            events.filter((event) => event.installerId === user.id).map((event) => {
                                                let isBlocked = event.ScheduleEventStatus.isBlocked;
                                                let topStart = getDayjsFromTime(event.datetimeStart, true).diff(activeDay.hour(workDayStartHour).minute(0), "minute") / 15;
                                                let heightDiff = getDayjsFromTime(event.datetimeEnd, false).diff(getDayjsFromTime(event.datetimeStart, true), "minute", true) / 15 - 0.05;

                                                return (
                                                    <div
                                                        key={event.id}
                                                        className={`cursor-pointer hover:opacity-75 active:opacity-100 relative flex flex-col col-span-full row-start-1 m-[1px] rounded-md drop-shadow-md px-1 py-[2px] text-light-11 event-container
                                                            bg-${statuses.find((s) => s.name === event.ScheduleEventStatus.name)?.style}
                                                        `}
                                                        style={{
                                                            top: `${ topStart }rem`,
                                                            height: `${ heightDiff }rem`,
                                                        }}
                                                        onClick={(e) => handleEventClick(e, event.id)}
                                                    >

                                                    <div className="flex flex-row">
                                                        <p className="text-xs font-bold">{ event.ScheduleEventType.name }</p>
                                                        <div className="ml-auto text-light-11">{eventIcons[event.ScheduleEventType.icon]?.icon}</div>
                                                    </div>

                                                    { (<p className="text-xs row-start-2">{isBlocked ? "" : event.customerName}</p>) }
                                                    { isBlocked && (<p className="text-xs row-start-2">{event.subEvent}</p>) }
                                                    { (<p className="text-xs row-start-2">{isBlocked ? "" : event.address}</p>) }
                                                </div>
                                            )})
                                        }
                                    </div>
                                ))
                            }
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}